import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api.js";

const C = {
  navy:"#0E1A2E", red:"#C8302A", amber:"#E8A317", green:"#0F8B3D",
  white:"#FFFFFF", cream:"#FAF7F1", border:"#E0D9C8", line:"#EBE4D2",
  text:"#1A1A1A", text2:"#3A3A3A", text3:"#6A6A6A", text4:"#9A9A9A",
};

export default function Track() {
  const [awb, setAwb] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!awb.trim()) return;
    setError(null); setResult(null); setBusy(true);
    try {
    const res = await fetch(`/track/${encodeURIComponent(awb.trim())}`);
        if (!res.ok) throw new Error(res.status === 404 ? "Not Found" : "Tracking failed");
        const result = await res.json();
      setResult(result);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleKey = (e) => { if (e.key === "Enter") submit(); };

  return (
    <div style={{ minHeight:"100vh", background:C.cream, fontFamily:"-apple-system,system-ui,Inter,sans-serif" }}>
      {/* Nav */}
      <div style={{ background:C.white, borderBottom:`1px solid ${C.line}`, padding:"16px 24px" }}>
        <div style={{ maxWidth:1100, margin:"0 auto", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <Link to="/" style={{ textDecoration:"none", display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ width:32, height:32, background:C.red, borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center", color:C.white, fontWeight:800, fontSize:16 }}>S</div>
            <div style={{ fontSize:17, fontWeight:800, color:C.navy }}>Shiftswift</div>
          </Link>
          <div style={{ display:"flex", gap:20, alignItems:"center" }}>
            <Link to="/login" style={{ fontSize:13, color:C.text2, textDecoration:"none", fontWeight:600 }}>Sign in</Link>
            <Link to="/join" style={{ background:C.red, color:C.white, padding:"9px 18px", borderRadius:7, textDecoration:"none", fontSize:13, fontWeight:700 }}>Open account</Link>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div style={{ background:`linear-gradient(135deg,${C.navy} 0%,#1B2A45 100%)`, padding:"64px 24px 56px", textAlign:"center", color:C.white }}>
        <div style={{ fontSize:11, color:C.amber, letterSpacing:2.5, fontWeight:700, marginBottom:14 }}>LIVE TRACKING</div>
        <h1 style={{ fontSize:"clamp(30px,5vw,52px)", fontWeight:800, margin:0, letterSpacing:-1.5 }}>Track your shipment</h1>
        <p style={{ fontSize:16, color:"rgba(255,255,255,0.75)", marginTop:14, maxWidth:480, margin:"14px auto 0" }}>
          Enter your AWB number or ShipSwift order number for live status.
        </p>

        <div style={{ maxWidth:560, margin:"36px auto 0", display:"flex", gap:10, flexWrap:"wrap" }}>
          <input
            type="text" value={awb}
            onChange={e => setAwb(e.target.value)}
            onKeyDown={handleKey}
            placeholder="e.g. EE123456789IN or SS-ABCD1234"
            style={{
              flex:1, minWidth:240, padding:"16px 18px",
              border:"none", borderRadius:8, fontSize:15,
              fontFamily:"inherit", outline:"none", background:C.white,
            }}
          />
          <button
            type="button"
            onClick={submit}
            disabled={busy || !awb.trim()}
            style={{
              background: busy || !awb.trim() ? C.text4 : C.red,
              color:C.white, border:"none", padding:"16px 32px",
              borderRadius:8, fontSize:15, fontWeight:700,
              cursor: busy || !awb.trim() ? "not-allowed" : "pointer",
              fontFamily:"inherit",
            }}
          >
            {busy ? "Tracking…" : "Track"}
          </button>
        </div>
      </div>

      {/* Results */}
      <div style={{ maxWidth:760, margin:"0 auto", padding:"32px 24px 60px" }}>
        {error && (
          <div style={{ background:"#FEF2F2", border:"1px solid #FECACA", color:"#991B1B", padding:16, borderRadius:8, fontSize:14 }}>
            {error}
          </div>
        )}

        {result && <TrackingResult data={result} />}

        {!result && !error && (
          <div style={{ marginTop:16, padding:28, background:C.white, border:`1px solid ${C.line}`, borderRadius:12 }}>
            <div style={{ fontSize:14, fontWeight:700, color:C.navy, marginBottom:10 }}>Where do I find my tracking number?</div>
            <div style={{ fontSize:13, color:C.text2, lineHeight:1.7 }}>
              Your AWB is in the booking confirmation email and on the shipping label.
              ShipSwift order numbers (e.g. <span style={{ fontFamily:"monospace", color:C.text }}>SS-ABCD1234</span>) also work.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TrackingResult({ data }) {
  const events = data.events || data.timeline || [];
  const status = (data.status || data.current_status || "in_transit").replace(/_/g, " ");
  const statusColor = status.includes("deliver") ? C.green : status.includes("cancel") ? "#991B1B" : C.amber;

  return (
    <div>
      {/* Status card */}
      <div style={{ background:C.white, borderRadius:12, padding:28, border:`1px solid ${C.line}`, marginBottom:16 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:12 }}>
          <div>
            <div style={{ fontSize:11, color:C.text3, fontWeight:700, letterSpacing:1.5, marginBottom:6 }}>AWB</div>
            <div style={{ fontSize:18, fontWeight:800, color:C.navy, fontFamily:"monospace", letterSpacing:0.5 }}>
              {data.awb || data.tracking_number || "—"}
            </div>
          </div>
          <div style={{ background:statusColor, color:C.white, padding:"6px 16px", borderRadius:20, fontSize:12, fontWeight:700, textTransform:"capitalize" }}>
            {status}
          </div>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))", gap:16, marginTop:20 }}>
          {data.receiver_city && (
            <Info label="Destination" val={`${data.receiver_city}${data.receiver_pincode ? ` – ${data.receiver_pincode}` : ""}`} />
          )}
          {data.estimated_delivery && (
            <Info label="Expected delivery" val={data.estimated_delivery} />
          )}
          {data.service_name && (
            <Info label="Service" val={data.service_name} />
          )}
        </div>
      </div>

      {/* Timeline */}
      {events.length > 0 && (
        <div style={{ background:C.white, borderRadius:12, padding:28, border:`1px solid ${C.line}` }}>
          <div style={{ fontSize:14, fontWeight:800, color:C.navy, marginBottom:20 }}>Tracking history</div>
          {events.map((ev, i) => (
            <div key={i} style={{ display:"flex", gap:16, marginBottom: i < events.length-1 ? 20 : 0 }}>
              <div style={{ display:"flex", flexDirection:"column", alignItems:"center" }}>
                <div style={{
                  width:10, height:10, borderRadius:5,
                  background: i === 0 ? C.red : C.line,
                  flexShrink:0, marginTop:3,
                }} />
                {i < events.length-1 && <div style={{ width:1, flex:1, background:C.line, margin:"4px 0" }} />}
              </div>
              <div style={{ flex:1, paddingBottom: i < events.length-1 ? 4 : 0 }}>
                <div style={{ fontSize:13, fontWeight:700, color:C.navy }}>
                  {(ev.status || ev.description || "").replace(/_/g, " ")}
                </div>
                {ev.location && <div style={{ fontSize:12, color:C.text3, marginTop:2 }}>{ev.location}</div>}
                {ev.event_time && <div style={{ fontSize:11, color:C.text4, marginTop:2 }}>{ev.event_time}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Info({ label, val }) {
  return (
    <div>
      <div style={{ fontSize:11, color:C.text3, fontWeight:600, marginBottom:3 }}>{label}</div>
      <div style={{ fontSize:14, fontWeight:700, color:C.navy }}>{val}</div>
    </div>
  );
}
